import { SESSION_PAGE_GUTTER, SESSION_PANEL_PADDING, SESSION_SECTION_STACK } from '../../utils/sessionMobileLayout';

function PulseBlock({ className = '' }) {
  return (
    <div
      className={`animate-pulse rounded-lg bg-surface-subtle ${className}`.trim()}
      aria-hidden
    />
  );
}

function CourtCardSkeleton() {
  return (
    <div className={`rounded-xl border border-border-medium bg-surface shadow-brand-sm ${SESSION_PANEL_PADDING}`}>
      <div className="mb-3 flex justify-between gap-2">
        <PulseBlock className="h-4 w-24" />
        <PulseBlock className="h-4 w-16" />
      </div>
      <div className="mb-2 border-t border-border-light pt-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <PulseBlock className="h-3 w-16" />
            <PulseBlock className="h-8 w-full" />
            <PulseBlock className="h-8 w-full" />
          </div>
          <div className="space-y-2">
            <PulseBlock className="h-3 w-16" />
            <PulseBlock className="h-8 w-full" />
            <PulseBlock className="h-8 w-full" />
          </div>
        </div>
      </div>
      <PulseBlock className="mx-auto mt-3 h-10 w-full max-w-xs" />
    </div>
  );
}

function BottomPanelSkeleton() {
  return (
    <div className={`flex flex-col rounded-xl border border-border-medium bg-surface-elevated/90 shadow-brand-sm dark:bg-surface/90 ${SESSION_PANEL_PADDING}`}>
      <PulseBlock className="mb-3 h-4 w-36" />
      <PulseBlock className="h-px w-full opacity-50" />
      <div className="mt-3 space-y-2">
        <PulseBlock className="h-8 w-full" />
        <PulseBlock className="h-8 w-full" />
      </div>
    </div>
  );
}

/**
 * Full-page shell while competitive session or player/court state is loading.
 * Mirrors the Courts tab layout (stacked: session bar → courts → next → waitlist chips → panels).
 */
export default function CompetitiveSessionLoadingSkeleton({ phase = 'data' }) {
  const statusMessage =
    phase === 'session' ? 'Loading session settings.' : 'Loading players and court state.';

  return (
    <div className="relative min-h-screen bg-background font-sans">
      <span className="sr-only" role="status">
        {statusMessage}
      </span>

      {/* Sticky banner placeholder */}
      <div className={`border-b border-border-medium bg-surface py-2.5 ${SESSION_PAGE_GUTTER}`}>
        <PulseBlock className="mx-auto h-10 max-w-3xl" />
      </div>

      <div className={`mx-auto max-w-full py-2 md:py-3 ${SESSION_PAGE_GUTTER}`}>
          <div className={`mb-2 rounded-lg border border-border-medium bg-surface shadow-brand-sm ${SESSION_PANEL_PADDING}`}>
            <PulseBlock className="h-7 w-56 max-w-[80%]" />
            <div className="mt-2 flex flex-wrap gap-2">
              <PulseBlock className="h-6 w-20" />
              <PulseBlock className="h-6 w-24" />
              <PulseBlock className="h-6 w-28" />
            </div>
          </div>

        <div className={`mt-2 ${SESSION_SECTION_STACK}`}>
          {/* Host session controls bar — single row segments */}
          <div className="mb-5 overflow-hidden rounded-lg border border-border-medium bg-surface text-xs shadow-brand-sm">
            <div className="flex flex-wrap items-stretch divide-x divide-border-medium md:flex-nowrap md:overflow-x-auto">
              <div className="flex shrink-0 items-center px-3 py-2.5">
                <PulseBlock className="h-6 w-28" />
              </div>
              <div className="flex min-w-0 shrink-0 items-center px-3 py-2.5">
                <PulseBlock className="h-6 w-36" />
              </div>
              <div className="flex shrink-0 items-center px-3 py-2.5">
                <PulseBlock className="h-6 w-24" />
              </div>
              <div className="flex shrink-0 items-center gap-2 px-3 py-2.5">
                <PulseBlock className="h-6 w-20" />
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4].map((i) => (
                    <PulseBlock key={i} className="h-7 w-7 rounded" />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Playing courts */}
          <div>
            <PulseBlock className="mb-3 h-7 w-40 max-w-[70%]" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <CourtCardSkeleton key={i} />
              ))}
            </div>
          </div>

          {/* Next on court */}
          <div>
            <PulseBlock className="mb-3 h-7 w-36 max-w-[60%]" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <CourtCardSkeleton key={i} />
              ))}
            </div>
          </div>

          {/* Waitlist — horizontal chips row */}
          <div className={`rounded-xl border border-border-medium bg-surface-elevated/90 shadow-brand-sm dark:bg-surface/90 ${SESSION_PANEL_PADDING}`}>
            <PulseBlock className="mb-3 h-5 w-44" />
            <PulseBlock className="mb-2 h-px w-full opacity-40" />
            <div className="flex flex-wrap gap-1.5 py-1">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                <PulseBlock key={i} className="h-8 w-[7.5rem] rounded-full" />
              ))}
            </div>
          </div>

          {/* Partnerships / scheduled / signed-up — bottom grid */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <BottomPanelSkeleton />
            <BottomPanelSkeleton />
            <BottomPanelSkeleton />
          </div>
        </div>
      </div>
    </div>
  );
}
